import { getConfig } from "./config";
import { csrfToken } from "@/auth/csrf";
import { CORRELATION_HEADER, newCorrelationId } from "./correlation";
import { logDiagnostic } from "./diagnostics";
import { CODES } from "@/lib/diagnostics";
import { markServerContact } from "./server-contact";

// Shared same-origin request boundary for the SPA's direct BFF calls (the
// non-Apollo paths: /auth/me, /ai/sessions, /ai/model-config, the SSE streams).
// It is the single seam that:
//   - prepends the runtime basePath,
//   - stamps a request-scoped UUIDv4 `X-Correlation-ID` (returned so a caller
//     can carry it into a diagnostic for a pre-BFF failure — ticket #124),
//   - forwards the session cookie (`credentials: "include"`),
//   - attaches the CSRF token for mutating requests.
//
// Deliberately NOT a global `fetch` monkey-patch: only Spark-owned same-origin
// endpoints flow through here, so third-party traffic can never become
// diagnostic input (ticket #126). Phase 4 hangs the HTTP diagnostic producer
// on this boundary.

export interface ApiRequestOptions extends Omit<RequestInit, "headers"> {
  headers?: Record<string, string>;
  /** Attach the CSRF token (required for mutating requests). */
  csrf?: boolean;
  /** Reuse a pre-generated correlation ID instead of minting a new one. */
  correlationId?: string;
}

export interface ApiRequestResult {
  response: Response;
  correlationId: string;
}

// Replace UUID / long-token path segments with `:id` so the diagnostic
// endpoint is a template — never a concrete thread/person/document ID.
const IDISH = /^(?:[0-9a-fA-F-]{16,}|\d{6,})$/;
function templatize(path: string): string {
  return path
    .split("/")
    .map((seg) => (IDISH.test(seg) ? ":id" : seg))
    .join("/");
}

// `path` is relative to the runtime basePath and must NOT start with "/"
// (basePath already ends with "/"), e.g. "auth/me" or "ai/sessions".
export async function apiRequest(
  path: string,
  opts: ApiRequestOptions = {},
): Promise<ApiRequestResult> {
  const { headers = {}, csrf = false, correlationId = newCorrelationId(), ...init } = opts;

  const finalHeaders: Record<string, string> = {
    ...headers,
    [CORRELATION_HEADER]: correlationId,
  };
  if (csrf) {
    const token = csrfToken();
    if (token) finalHeaders["X-CSRF-Token"] = token;
  }

  const method = (init.method ?? "GET").toUpperCase();
  let response: Response;
  try {
    response = await fetch(`${getConfig().basePath}${path}`, {
      credentials: "include",
      ...init,
      headers: finalHeaders,
    });
  } catch (err) {
    // Transport failure before any response. Report a safe http diagnostic and
    // re-throw so callers see the original error (unchanged behavior).
    logDiagnostic({
      level: "error",
      code: CODES.http.REQUEST_FAILURE,
      category: "http",
      correlationId,
      details: { endpoint: templatize(path), method: httpMethod(method), phase: "request" },
    });
    throw err;
  }

  markServerContact();

  // Only server-side failures are diagnostics; expected 4xx domain outcomes
  // (e.g. model-config 403/501) are left to the caller.
  if (response.status >= 500) {
    logDiagnostic({
      level: "error",
      code: CODES.http.REQUEST_FAILURE,
      category: "http",
      correlationId,
      details: {
        endpoint: templatize(path),
        method: httpMethod(method),
        status: response.status,
        phase: "response",
      },
    });
  }

  return { response, correlationId };
}

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"] as const;
function httpMethod(m: string): (typeof HTTP_METHODS)[number] | undefined {
  return (HTTP_METHODS as readonly string[]).includes(m)
    ? (m as (typeof HTTP_METHODS)[number])
    : undefined;
}
